import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { PropertyFeatures } from '../PropertyFeaturesDialog';

const FEATURE_CATEGORIES: { title: string; key: string; items: string[] }[] = [
  { title: 'Main Features', key: 'main', items: ['Central Air Conditioning', 'Central Heating', 'Double Glazed Windows', 'Waste Disposal', 'Electricity Backup / Generator', 'Furnished', 'Semi-Furnished', 'Marble / Tile Flooring', 'Wooden Flooring', 'Built-in Wardrobes', 'Solar Panels', 'Water Tank / Cistern'] },
  { title: 'Rooms', key: 'rooms', items: ['Drawing Room', 'Dining Room', 'Kitchen', 'Study Room / Office', 'Prayer Room', 'Gymnasium / Gym', 'Store Room', 'Laundry Room', 'Servant Quarter', 'Guest Room', 'Balcony'] },
  { title: 'Business & Communication', key: 'business', items: ['Broadband Internet', 'Satellite / Cable TV', 'Intercom', 'Conference Room', 'CCTV Security', 'Fire Alarm System'] },
  { title: 'Healthcare & Recreational', key: 'recreation', items: ['Lawn / Garden', 'Swimming Pool', 'Sauna / Steam Room', 'Jacuzzi', 'Kids Play Area', 'Barbecue Area', 'Community Hall', 'Rooftop Terrace'] },
  { title: 'Nearby Locations & Facilities', key: 'nearby', items: ['Near Schools', 'Near Hospitals', 'Near Shopping Malls', 'Near Restaurants', 'Near Airport', 'Near Public Transport', 'Near Parks', 'Near Places of Worship'] },
  { title: 'Other Facilities', key: 'other', items: ['Maintenance Staff', 'Security Staff / Guard', 'Facilities for Disabled', 'Elevator / Lift', 'Parking (Covered)', 'Parking (Uncovered)', 'Pet Friendly', 'Gated Community'] },
];

interface Props {
  features: PropertyFeatures;
  setFeatures: (f: PropertyFeatures) => void;
}

export function Step4Features({ features, setFeatures }: Props) {
  const toggle = (cat: string, item: string) => {
    const arr = features[cat] || [];
    setFeatures({
      ...features,
      [cat]: arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item],
    });
  };

  const total = Object.values(features).reduce((s, a) => s + (a?.length || 0), 0);

  return (
    <>
      <p className="text-sm text-muted-foreground">
        Selected features help buyers find your property. <strong className="text-foreground">{total}</strong> selected.
      </p>
      <Accordion type="multiple" defaultValue={['main']} className="w-full">
        {FEATURE_CATEGORIES.map(cat => {
          const count = (features[cat.key] || []).length;
          return (
            <AccordionItem key={cat.key} value={cat.key}>
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2">
                  <span>{cat.title}</span>
                  {count > 0 && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">{count}</span>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                  {cat.items.map(item => {
                    const checked = (features[cat.key] || []).includes(item);
                    return (
                      <label key={item} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/50 rounded px-2 py-1.5">
                        <Checkbox checked={checked} onCheckedChange={() => toggle(cat.key, item)} />
                        <span>{item}</span>
                      </label>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </>
  );
}
